<template>
	<article class="new-event-card" :class="{'new-event-card--past': isPastEvent}">
		<header class="new-event-card__period">
			<img class="new-event-card__calendar-icon" src="/static/buttons/new/calendar.svg" alt="">
			<span>{{periodLabel}}</span>
		</header>

		<div class="new-event-card__logo">
			<img v-if="eventTranslation.imagePath" :src="getStorageUrl(eventTranslation.imagePath)" :alt="eventTranslation.name">
			<div v-else class="new-event-card__placeholder">{{eventInitials}}</div>
		</div>

		<div class="new-event-card__body">
			<h3 :title="eventTranslation.name">{{eventTranslation.name}}</h3>
			<p v-if="locationName" class="new-event-card__location">
				<img class="new-event-card__local-icon" src="/static/buttons/new/local.svg" alt="">
				<span>{{locationName}}</span>
			</p>
		</div>

		<router-link
			v-if="!isPastEvent"
			class="new-event-card__action"
			:to="setObjEvent('event', {shortName: event.shortName, event: event})"
		>
			{{eventCardTranslation.actionLabel}}
		</router-link>
		<span v-else class="new-event-card__action new-event-card__action--disabled">{{eventCardTranslation.pastLabel}}</span>
	</article>
</template>

<script>
import moment from 'moment';
import { formatDate } from '../../assets/format';
import { translate } from '../../assets/translations';
import { getStorageUrl } from '../../util';

export default {
	props: {
		event: {
			type: Object,
			required: true,
		},
	},
	computed: {
		eventTranslation() {
			const langId = this.$store.state.language.id;
			const fallback = { name: '', imagePath: '' };
			if (!this.event.translations) return fallback;
			return this.event.translations.find(lang => lang.language.id === langId) || fallback;
		},
		eventCardTranslation() {
			return translate(this.$store.state.language).eventCard;
		},
		periodLabel() {
			return `${this.formatDate(this.event.startDate)} ${this.eventCardTranslation.betweenDatesText} ${this.formatDate(this.event.endDate)}`;
		},
		locationName() {
			return this.event.local ? this.event.local.name : '';
		},
		isPastEvent() {
			const endDate = moment(this.event.endDate);
			return endDate.isValid() && moment().isAfter(endDate.endOf('day'));
		},
		eventInitials() {
			const name = this.eventTranslation.name || 'BL';
			return name
				.split(' ')
				.filter(Boolean)
				.slice(0, 2)
				.map(part => part.charAt(0))
				.join('')
				.toUpperCase();
		},
	},
	methods: {
		getStorageUrl,
		formatDate(date) {
			return formatDate(date, 'DD/MM');
		},
		setObjEvent(name, params) {
			return { name, params };
		},
	},
};
</script>

<style scoped lang="scss">
.new-event-card {
	display: flex;
	flex-direction: column;
	box-sizing: border-box;
	min-height: 33.45vw;
	padding: 7.6% 14.6%;
	background: #ffffff;
	border: 1px solid #d9d9d9;
	border-radius: 2.49em;
	box-shadow: 4px 4px 15px 2px rgba(0, 0, 0, .1);
	color: #4c4b4b;
	font-size: 1vw;
	transition: transform .24s ease, box-shadow .24s ease;
}

.new-event-card:hover {
	transform: translateY(-3px);
	box-shadow: 6px 8px 18px 2px rgba(0, 0, 0, .13);
}

.new-event-card--past {
	opacity: .42;
}

.new-event-card--past:hover {
	transform: none;
	box-shadow: 4px 4px 15px 2px rgba(0, 0, 0, .1);
}

.new-event-card__period {
	display: flex;
	justify-content: center;
	align-items: center;
	gap: .8em;
	min-height: 1.51em;
	padding-bottom: .89em;
	border-bottom: 1px solid #9a9595;
	color: #4c4b4b;
	font-size: 2.03em;
	font-weight: 600;
	line-height: 1.17;
}

.new-event-card__calendar-icon {
	display: block;
	width: 1.57em;
	height: 1.49em;
	object-fit: contain;
}

.new-event-card__logo {
	display: flex;
	align-items: center;
	justify-content: center;
	box-sizing: border-box;
	height: 10.24em;
	padding: .58em 0;
	border-bottom: 1px solid #9a9595;
}

.new-event-card__logo img {
	display: block;
	max-width: 100%;
	max-height: 8.97em;
	object-fit: contain;
}

.new-event-card__placeholder {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 3.45em;
	height: 3.45em;
	border: 1px solid #d7edf4;
	border-radius: 50%;
	color: #5da3bd;
	font-size: 1.27em;
	font-weight: 800;
}

.new-event-card__body {
	flex: 1;
	padding: 1.85em 1.16em 0;
}

.new-event-card h3 {
	display: -webkit-box;
	min-height: 3.54em;
	max-height: 3.54em;
	margin: 0 0 1.54em;
	overflow: hidden;
	color: #4c4b4b;
	font-size: 1.62em;
	font-weight: 500;
	line-height: 1.18;
	-webkit-box-orient: vertical;
	-webkit-line-clamp: 3;
}

.new-event-card__location {
	display: flex;
	align-items: flex-start;
	gap: .35em;
	margin: 0 0 2.6em;
	color: #4c4b4b;
	font-size: 1.16em;
	font-weight: 400;
	line-height: 1.4;
}

.new-event-card__local-icon {
	display: block;
	flex: 0 0 auto;
	width: 1.3em;
	height: 1.3em;
	object-fit: contain;
}

.new-event-card__action {
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 2.94em;
	padding: 0 .89em;
	border-radius: .5em;
	background: #e89127;
	color: #f8f8f8;
	font-size: 1.04em;
	font-weight: 400;
	line-height: 1.17;
	text-align: center;
	text-decoration: none;
}

.new-event-card__action:hover {
	background: #d57f20;
	color: #f8f8f8;
}

.new-event-card__action--disabled {
	background: #cfcfcf;
}

.new-event-card__action--disabled:hover {
	background: #cfcfcf;
}

@media (max-width: 1000px) {
	.new-event-card {
		min-height: 50.9vw;
		font-size: 1.5vw;
	}
}

@media (max-width: 650px) {
	.new-event-card {
		min-height: 103.8vw;
		font-size: 3.1vw;
	}
}

@media (max-width: 520px) {
	.new-event-card {
		min-height: 19.7rem;
		padding: 1.5rem 1.625rem;
		border-radius: 1.5rem;
		font-size: 1rem;
	}

	.new-event-card__period {
		gap: .625rem;
		min-height: 2.125rem;
		padding: 0 0 1.125rem;
		font-size: 1.5rem;
		line-height: 1.5;
	}

	.new-event-card__calendar-icon {
		width: 1.75rem;
		height: 1.75rem;
	}

	.new-event-card__logo {
		height: 10.375rem;
		padding: .875rem 0;
	}

	.new-event-card__logo img {
		max-height: 8.5rem;
	}

	.new-event-card__body {
		padding: 1.25rem 0 1.125rem;
	}

	.new-event-card h3 {
		margin-bottom: 1.125rem;
		font-size: 1.125rem;
	}

	.new-event-card__location {
		margin-bottom: 1.5rem;
		font-size: .875rem;
		line-height: 1.25;
	}

	.new-event-card__local-icon {
		width: 1.25rem;
		height: 1.25rem;
	}

	.new-event-card__action {
		min-height: 2.75rem;
		font-size: .875rem;
	}
}
</style>



// WEBPACK FOOTER //
// src/components/home/EventCard.vue